import React from "react";
import {
  Input,
  InputGroup,
  Helper,
  ITooltipContent,
  requiredNonEmptyString,
  WatchedFunction,
  IGroupValidationResult,
} from "component-library";
import { dataTestIds } from "data-testids";

import { IEditAudioFormContent, getTooltipProps } from "./EditAudioFormContent";
import { TrackViewer } from "../TrackViewer";
import { MediaAsset } from "mediaModules/media/models";
import { ShimmeringStub } from "mediaModules/media/components/ShimmeringStub";

import "./EditAudioForm.scss";

const VALIDATE = new WatchedFunction(() => undefined);
const TITLE_MAX_LENGTH = 255;

export interface IEditAudioFormProps {
  content: IEditAudioFormContent;
  audioModel: MediaAsset;
  changeModel: (videoModel: MediaAsset) => void;
  saveChanges: () => void;
  showMediaPopup: (audioModel: MediaAsset) => void;
  children: (submit: () => void) => React.ReactElement;
  isMediaMetadataLoading: boolean;
}

export interface IEditAudioFormState {
  isFormSubmitted: boolean;
  validationData: {
    title: {
      invalid: boolean;
      validationMessage: string;
    };
  };
}

export class EditAudioForm extends React.Component<
  IEditAudioFormProps,
  IEditAudioFormState
> {
  private isValidationChanged: boolean = false;
  private initialTitleValue: string = this.props.audioModel.title || "";

  public readonly state: Readonly<IEditAudioFormState> = {
    validationData: {
      title: {
        invalid: false,
        validationMessage: "",
      },
    },
    isFormSubmitted: false,
  };

  public render() {
    const { children, isMediaMetadataLoading } = this.props;

    return (
      <React.Fragment>
        {isMediaMetadataLoading
          ? this.renderMetadataLoading()
          : this.renderMediaAsset()}
        {children(this.saveChanges)}
      </React.Fragment>
    );
  }

  public componentDidUpdate() {
    if (this.isValidationChanged) {
      this.isValidationChanged = false;
      VALIDATE.call();
    }
  }

  private renderMetadataLoading = () => {
    return (
      <div>
        <div className="g-content-area c-edit-audio-form c-edit-audio-form--loading">
          <div className="g-col-md-6 g-col-lg-6">
            <ShimmeringStub />
          </div>
        </div>
        <div className="g-col-md-8 g-col-lg-7 c-edit-audio-form__input--loading" />
      </div>
    );
  };

  private renderMediaAsset = () => {
    const { content, audioModel, showMediaPopup } = this.props;
    const { validationData, isFormSubmitted } = this.state;

    const formConfig = this.setFormConfig();

    return (
      <div className="g-content-area c-edit-audio-form">
        <div className="g-col-md-6 g-col-lg-6">
          <TrackViewer
            title={this.initialTitleValue}
            audioModel={audioModel}
            playback={showMediaPopup.bind(this, audioModel)}
            titleMaxLines={3}
          />
        </div>
        <div className="g-col-md-8 g-col-lg-7" />
        <div className="g-col-md-6 g-col-lg-6">
          <InputGroup
            className="c-edit-audio-form__control"
            inputConfigs={formConfig}
            validationDone={this.validationPerformed}
            triggerValidation={VALIDATE}
          >
            <Input
              id="title"
              className="c-edit-audio-form__input"
              type="text"
              value={audioModel.title}
              name="title"
              label={content.audioTitleFieldLabel}
              invalid={validationData.title.invalid}
              validationMessage={validationData.title.validationMessage}
              isFormSubmitted={isFormSubmitted}
              annex={this.getTitleAnnex(content.audioTitleFieldTooltip)}
              valueChanged={this.changeTitle}
              maxLength={TITLE_MAX_LENGTH}
              disabled={audioModel.isMediaObjectLoading}
              data-testid={
                dataTestIds.profile["Media.AudioForm.EditTitleInput"]
              }
            />
          </InputGroup>
        </div>
      </div>
    );
  };

  private changeTitle = (title: string) => {
    this.isValidationChanged = true;
    this.props.changeModel({ ...this.props.audioModel, title });
    VALIDATE.call();
  };

  private getTitleAnnex = (content: ITooltipContent) => (
    <Helper tooltip={getTooltipProps(content)} />
  );

  private saveChanges = () => {
    this.setState({ isFormSubmitted: true }, () => {
      const { validationData } = this.state;
      const invalidForm = Object.keys(validationData).some(
        (key) => validationData[key].invalid
      );

      if (!invalidForm) {
        this.props.saveChanges();
      }
    });
  };

  private validationPerformed = (result: IGroupValidationResult) => {
    const validationData = {
      ...this.state.validationData,
      ...result.validationData,
    };
    this.setState({ validationData });
  };

  private setFormConfig = () => {
    return [
      {
        name: "title",
        value: this.props.audioModel.title,
        validators: [
          requiredNonEmptyString(this.props.content.audioTitleRequired),
        ],
      },
    ];
  };
}
